import React, { memo, useRef, useMemo, useState, useCallback } from "react";
import { FormControl, TextField, InputAdornment } from "@mui/material";
import Popover from "@mui/material/Popover";
import PropTypes from "prop-types";

import classNames from "common/class-names";
import { TIME_FORMAT, parseTime, formatTime, formatTimeValue, getHourOptions, getMinuteOptions } from "common/time";
import AppIcon from "components/app-icon";
import AppCloseIcon from "components/icons/app-close-icon";
import AppClockIcon from "components/icons/app-clock-icon";
import AppTimeColumn from "components/app-time-input/app-time-column";
import lockIcon from "assets/images/components/app-input/lock-icon.svg";

const AppTimeInput = (props) => {
	const TYPES = useMemo(() => ({ REMOVE: "REMOVE", ADD: "ADD" }), []);
	const anchorRef = useRef();
	const [visible, setVisible] = useState(false);
	const minuteStep = props.minuteStep || 1;
	const clearable = props.clearable !== false;
	const lockMinute = !!props.lockMinute;
	const withSeconds = props.valueFormat === TIME_FORMAT.TIME_SECONDS_FORMAT;
	const currentTime = useMemo(() => formatTimeValue(props.value), [props.value]);
	const isErrorField = useMemo(() => !!props.error && !!props.touched, [props.error, props.touched]);
	const errorMessage = useMemo(() => (isErrorField ? props.error : ""), [props.error, isErrorField]);

	const [time, setTime] = useState({ hour: 0, minute: 0 });
	const hours = useMemo(() => getHourOptions(props.minTime, props.maxTime), [props.minTime, props.maxTime]);
	const minutes = useMemo(() => {
		if (lockMinute) return [{ value: 0, label: "00", isDisabled: true }];

		return getMinuteOptions(time.hour, minuteStep, props.minTime, props.maxTime);
	}, [lockMinute, time.hour, minuteStep, props.minTime, props.maxTime]);

	const className = useMemo(() => {
		return classNames({
			"app-time-input": true,
			"app-time-input--disabled": props.disabled,
			...(props.className && {
				[props.className]: true,
			}),
		});
	}, [props.className, props.disabled]);

	const onHandleDismiss = useCallback(() => {
		setVisible(false);
	}, []);

	//prettier-ignore
	const onHandleTime = useCallback((event) => {
		const type = event.target.getAttribute("data-type");

		if(type === TYPES.REMOVE) {
			event.stopPropagation();
			props.onChange(props.name, "");
		}

		if(type === TYPES.ADD) {
			if (props.disabled) return;

			setTime(parseTime(props.value) || parseTime(props.minTime) || { hour: 0, minute: 0 });

			setVisible(true);
		}
	}, [TYPES, props]);

	//prettier-ignore
	const onHandleSelectHour = useCallback((hour) => {
		const options = getMinuteOptions(hour, minuteStep, props.minTime, props.maxTime);
		const isSelectable = options.some((o) => o.value === time.minute && !o.isDisabled);
		const fallback = options.find((o) => !o.isDisabled);
		const minute = isSelectable ? time.minute : fallback ? fallback.value : 0;

		setTime({ hour, minute });

		props.onChange(props.name, formatTime(hour, minute, withSeconds));
	}, [props, time.minute, minuteStep, withSeconds, lockMinute]);

	//prettier-ignore
	const onHandleSelectMinute = useCallback((minute) => {
		setTime((prev) => ({ ...prev, minute }));

		props.onChange(props.name, formatTime(time.hour, minute, withSeconds));
	}, [props, time.hour, withSeconds]);

	const InputProps = useMemo(() => {
		const inputProps = {};

		const endAdornment = {};

		if (props.disabled) {
			endAdornment.endAdornment = (
				<InputAdornment position="end">
					<AppIcon src={lockIcon} />
				</InputAdornment>
			);
		} else if (props.value && clearable) {
			endAdornment.endAdornment = (
				<InputAdornment position="end" data-type={TYPES.REMOVE}>
					<AppCloseIcon className="app-time-input__icon" data-type={TYPES.REMOVE} color="#F64444" onClick={onHandleTime} />
				</InputAdornment>
			);
		} else {
			endAdornment.endAdornment = (
				<InputAdornment position="end">
					<span className="app-time-input__clock">
						<AppClockIcon color="#666666" />
					</span>
				</InputAdornment>
			);
		}

		Object.assign(inputProps, endAdornment);

		return inputProps;
	}, [TYPES, props.disabled, props.value, clearable, onHandleTime]);

	return (
		<div className={className}>
			<FormControl error={isErrorField}>
				<label className="app-time-input__label" htmlFor={props.name}>
					{props.label}
					{props.required && <span className="app-time-input__required">*</span>}
				</label>

				<div aria-hidden="true" ref={anchorRef} className="app-time-input__wrapper" data-type={TYPES.ADD} onClick={onHandleTime}>
					<TextField autoComplete="off" value={currentTime} type="text" name={props.name} error={isErrorField} helperText={errorMessage} disabled placeholder={props.placeholder} InputProps={InputProps} />
				</div>
			</FormControl>

			<Popover classes={{ root: "app-time-input-modal" }} open={visible} anchorEl={anchorRef.current} onClose={onHandleDismiss} anchorOrigin={{ vertical: "bottom", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "left" }} slotProps={{ paper: { style: { width: anchorRef.current?.offsetWidth } } }}>
				<div className="time-dropdown">
					<div className="time-dropdown__headers">
						<p className="time-dropdown__header">Hour</p>
						<p className="time-dropdown__header">Minute</p>
					</div>

					<div className="time-dropdown__columns">
						<span aria-hidden="true" className="time-dropdown__band" />

						<AppTimeColumn value={time.hour} options={hours} onChange={onHandleSelectHour} />

						<AppTimeColumn value={time.minute} options={minutes} onChange={onHandleSelectMinute} />
					</div>
				</div>
			</Popover>
		</div>
	);
};

AppTimeInput.propTypes = {
	error: PropTypes.string,
	label: PropTypes.string,
	touched: PropTypes.bool,
	disabled: PropTypes.bool,
	required: PropTypes.bool,
	clearable: PropTypes.bool,
	lockMinute: PropTypes.bool,
	className: PropTypes.string,
	placeholder: PropTypes.string,
	value: PropTypes.string,
	minTime: PropTypes.string,
	maxTime: PropTypes.string,
	minuteStep: PropTypes.number,
	valueFormat: PropTypes.oneOf([TIME_FORMAT.TIME_FORMAT, TIME_FORMAT.TIME_SECONDS_FORMAT]),
	name: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
};

export default memo(AppTimeInput);
